import { memo, useCallback, useEffect, useRef } from 'react'

import { ContentExternalStoreEdit } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { useExternalStoreUpdateMutation } from 'hooks/mutations/content'
import { externalStoreConfigRoute } from 'pages/NWPlatform/ContentStores/ExternalStores/ExternalStore'
import { externalStoreQueryKeys } from 'services/queryKeys'

const ConfigRaw = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('domain')

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const { externalStoreId } = externalStoreConfigRoute.useParams()

  const { data, isLoading } = useQuery(externalStoreQueryKeys.detail({ storeId: externalStoreId }))

  const { mutate: updateExternalStore } = useExternalStoreUpdateMutation()

  const handleSave = useCallback(
    (value: ContentExternalStoreEdit) => {
      updateExternalStore(
        { storeId: externalStoreId, contentExternalStoreEdit: value },
        {
          onSuccess: () => {
            enqueueSnackbar(t('ContentStores.externalStoreSaved'), {
              variant: 'success',
            })
          },
          onError: () => {
            enqueueSnackbar(t('ContentStores.errorCreateRetry'), {
              variant: 'error',
            })
          },
        },
      )
    },
    [enqueueSnackbar, externalStoreId, updateExternalStore, t],
  )

  useEffect(() => {
    if (editorInstanceRef.current && data) {
      editorInstanceRef.current.getModel()?.setValue(window.JSON.stringify(data, null, 4))
    }
  }, [data])

  if (isLoading) {
    return <LoadingIndicator />
  }

  if (!data) {
    return <NotFound />
  }

  return (
    <Editor
      value={data}
      onSubmit={handleSave}
      getEditorInstance={(editorInstance: EditorInstance) => (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const Config = memo(ConfigRaw)
